
import SurfaceHeader from 'vuecomponents/SurfaceHeader.vue'
import SurfacePosts from 'vuecomponents/surface_posts.vue'
import DoubleClickToPostMixin from 'vuemixins/double_click_to_post_mixin'
import DropToPostMixin from 'vuemixins/drop_to_post_mixin'
import loadMapApi from 'bits/map_api_loader'
import { listenScrollHeader, unlistenScrollHeader } from 'bits/header_scrolling'
import { subscribeUpdateBodySizeCss, unsubscribeUpdateBodySizeCss } from 'bits/body_sizing'
import window from 'bits/global'
import { RootState } from 'vuexstore/surface/types'
import {
  extractPostAttributesArrayFromClipboardData,
  extractPostAttributesFromClipboardData,
  PasteType,
} from 'bits/clipboard'
import { trackEvent } from 'bits/analytics'
import { isActiveElementAnInputField, isEventOnAnInputField } from 'bits/dom'
import tinykeys from 'tinykeys'
import initScroll, { ScrollDirection } from 'bits/scroll'

import { Component, Watch, Mixins } from 'vue-property-decorator'
import { namespace, Action, Getter, State } from 'vuex-class'

const postStore = namespace('post')
const snackbarsStore = namespace('snackbars')
const mapStore = namespace('map')
const draftStore = namespace('draft')
const composerModalStore = namespace('composerModal')

@Component({
  components: {
    SurfaceHeader,
    SurfacePosts,
    // SurfaceHeaderCanvas: () => import('vuecomponents/SurfaceHeaderCanvas.vue'),
    SurfaceActionBar: () => import('vuecomponents/SurfaceActionBar.vue'),
    SurfaceContainerGrid: () => import('vuecomponents/surface_container_grid.vue'),
    SurfaceContainerShelf: () => import('vuecomponents/surface_container_shelf.vue'),
    SurfaceContainerMap: () => import('vuecomponents/surface_container_map.vue'),
    SurfaceContainerTable: () => import('vuecomponents/surface_container_table.vue'),
    SurfaceContainerTimeline: () => import('vuecomponents/surface_container_timeline.vue'),
  },
  mixins: [DoubleClickToPostMixin, DropToPostMixin],
})
export default class SurfaceContainer extends Mixins() {
  isMapApiLoaded = false
  removeKeyboardShortcutsListener = (): void => {}

  @State isEmbedded
  @State isMobile
  @State isOnline
  @State isRefreshPending
  @State((state: RootState) => state.constants.googleApiKeyForMap)
  googleApiKeyForMap

  @State((state: RootState) => state.constants.currentCountryCode)
  currentCountryCode

  @Getter amIOwner
  @Getter backgroundLuminance
  @Getter dir
  @Getter format
  @Getter isAPanelVisible
  @Getter isApp
  @Getter isCanvas
  @Getter isGrid
  @Getter isMap
  @Getter isPostable
  @Getter isReadOnly
  @Getter isScreenshotMode
  @Getter isShelf
  @Getter isStream
  @Getter isSubscriptionPaused
  @Getter isTable
  @Getter isTimeline
  @Getter xDesktopSurfaceLayoutV3
  @Getter xHeader
  @Getter xMobileSurfaceLayoutV3
  @Getter headerHeight
  @Getter headerColor!: string

  @Action requestRefresh
  @Action refresh
  @Action resizeHeader

  @postStore.Getter postBeingEditedCid
  @postStore.Action createPost
  @postStore.Action startNewPost

  @snackbarsStore.Getter xSnackbar
  @snackbarsStore.Action setSnackbar

  @mapStore.Action startPickingLocation
  @draftStore.Action startNewDraft
  @composerModalStore.Action('dock') dockComposerModal!: () => void

  // From mixins
  enableDoubleClickToPost!: (wallContainer: Element, wishList?: Element) => void // from DoubleClickToPostMixin
  disableDoubleClickToPost!: (wallContainer: Element) => void // from DoubleClickToPostMixin
  enableDropToPost!: (wallContainer: Element, callback?: (NewPost) => void) => void // from DropToPostMixin
  disableDropToPost!: (wallContainer: Element) => void // from DropToPostMixin

  $refs!: {
    wallContainer: HTMLDivElement
    surfaceHeader: SurfaceHeader
  }

  @Watch('isOnline')
  refreshIfPending(isOnline): void {
    if (isOnline && this.isRefreshPending) this.refresh({ isAutoRefresh: true })
  }

  @Watch('isPostable')
  togglePostHandlers(newValue): void {
    if (newValue) {
      this.enableNewPostHandlers()
    } else {
      this.disableNewPostHandlers()
    }
  }

  @Watch('format')
  loadNewFormat(newValue, oldValue): void {
    if (newValue !== oldValue) {
      // Allow native bridge messages to flush before reloading
      this.$nextTick(() => window.location.reload())
    }
  }

  async mounted(): Promise<void> {
    this.requestRefresh({ isAutoRefresh: false })
    if (this.isMap) {
      await loadMapApi(this.googleApiKeyForMap, {
        region: this.currentCountryCode,
      })
      this.isMapApiLoaded = true
    } else {
      this.$nextTick(() => {
        if (this.isPostable) {
          this.enableNewPostHandlers()
        }
      })
    }
    if (!this.xHeader) {
      // mobile app with native header
      this.resizeHeader({ height: 0 })
    }
    if (this.isCanvas && this.xHeader) {
      this.$nextTick(() => {
        listenScrollHeader(this.$refs.wallContainer, this.$refs.surfaceHeader.$el as HTMLElement)
      })
    }
    // Ensure that export as image captures all posts
    if (this.isReadOnly && (this.isGrid || this.isCanvas)) {
      const hookToAllStoreChanges = ((store) => store.subscribe.bind(store))(this.$store)
      const events = ['load', 'beethoven-load', 'error', 'beethoven-error', hookToAllStoreChanges]
      const extraMarginInPx = 200
      subscribeUpdateBodySizeCss('wish', events, extraMarginInPx)
    }
    if (this.isSubscriptionPaused && this.amIOwner) {
      const snackbarData: {
        message?
        persist?
        showCloseButton?
        actionText?
        actionUrl?
      } = {
        message: this.__('Your subscription is paused. Your padlets are read-only until it resumes.'),
        persist: true,
        showCloseButton: true,
      }
      if (!this.isApp) {
        snackbarData.actionText = this.__('Go to Settings')
        snackbarData.actionUrl = '/dashboard/settings/billing'
      }
      this.setSnackbar(snackbarData)
    }

    this.removeKeyboardShortcutsListener = tinykeys(window, {
      KeyC: this.handleOnKeydownC,
    })
    // 22 Aug 2022: revert back to let header scroll with page but still collapsible PRO-3514
    // if (!this.$refs.wallContainer || !this.$refs.surfaceHeader) return
    // const stopTrackingScroll = initScroll(
    //   this.$refs.wallContainer as HTMLElement,
    //   ({ direction }: { direction: ScrollDirection }): void => {
    //     if (direction.top === -1 || this.isCanvas) {
    //       this.$refs.surfaceHeader.collapse()
    //       stopTrackingScroll()
    //     }
    //   },
    // )
  }

  beforeDestroy(): void {
    this.disableNewPostHandlers()
    unlistenScrollHeader()
    unsubscribeUpdateBodySizeCss()
    this.removeKeyboardShortcutsListener()
  }

  enableNewPostHandlers(): void {
    if (this.isShelf || this.isGrid || this.isTable || this.isMap || this.isTimeline) return
    if (!this.isMobile) this.enableDoubleClickToPost(this.$el)
    this.enableDropToPost(this.$el)
    window.addEventListener('paste', this.handleOnPaste as (e: Event) => void)
  }

  disableNewPostHandlers(): void {
    this.disableDoubleClickToPost(this.$el)
    this.disableDropToPost(this.$el)
    window.removeEventListener('paste', this.handleOnPaste as (e: Event) => void)
  }

  handleOnPaste(event: ClipboardEvent): void {
    event.stopPropagation()
    if (!this.isPostable) return

    if (isEventOnAnInputField(event)) {
      return
    }

    const data = extractPostAttributesArrayFromClipboardData(event.clipboardData)
    if (!data || !data.length) return

    // Pasting texts from some applications like Microsoft Word may also paste an image version of the text
    // we want to treat them as text
    const isRichTextPastedAsFile =
      event.clipboardData?.types?.includes('text/rtf') && event.clipboardData?.types?.includes('Files')

    // Create a post with attachment without filename
    // when PasteType is File
    const attachment = extractPostAttributesFromClipboardData(data, PasteType.File)

    if (attachment && !isRichTextPastedAsFile) {
      this.startNewDraft({
        attributes: attachment.postAttributes,
        shouldStartEditing: true,
      })
      this.dockComposerModal()

      trackEvent('Posts', 'add', `Paste ${attachment.pasteType}`)
      return
    }

    // Create a new post when PasteType is Link or Text or PostContentHash
    const dataWithoutFile = data.filter((datum) => {
      return datum?.pasteType !== PasteType.File
    })

    this.startNewDraft({
      attributes: dataWithoutFile[0].postAttributes,
      shouldStartEditing: true,
    })
    this.dockComposerModal()

    trackEvent('Posts', 'add', `Paste ${dataWithoutFile[0].pasteType}`)
  }

  handleOnKeydownC(event: KeyboardEvent): void {
    if (this.postBeingEditedCid) return
    if (this.isAPanelVisible) return
    if (isActiveElementAnInputField()) return
    event.preventDefault()
    if (this.isMap) {
      this.startPickingLocation()
    } else {
      this.startNewPost()
    }
  }

  headerBackgroundTop = 0

  pageScrollHandlerTicking = false

  // Set header underneath background top position to hide it when the page scrolls the header and wishlist away
  handlePageScroll(): void {
    if (!this.pageScrollHandlerTicking) {
      window.requestAnimationFrame(() => {
        this.headerBackgroundTop = -(this.$refs.wallContainer || document.querySelector('#wall-container')).scrollTop
        this.pageScrollHandlerTicking = false
      })

      this.pageScrollHandlerTicking = true
    }
  }
}
